import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { MultiSelect } from "../components/ui/multi-select";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Briefcase, Loader2 } from "lucide-react";
import { JobRoleTitle, JobRoleTitle as JobRoleTitleType } from "../types";
import { Combobox } from "../components/ui/combobox";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { LoadingPageSkeleton } from "../components/common/LoadingPageSkeleton";
import AccessRestricted from "../components/AccessRestricted";

const getJobRole = async (id: string) => {
  return await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/jobroles/${id}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  });
};

export default function EditJobRolePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { jobRoles, departments, users, updateJobRole, qualificationPresets, mapJobRole, jobTitles, fetchJobTitles, jobGrades, fetchJobGrades } = useData();
  const { currentUser } = useAuth();

  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [jobRole, setJobRole] = useState<any>(null);

  const [title, setTitle] = useState<JobRoleTitleType>("" as JobRoleTitleType);
  const [description, setDescription] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [keyDuties, setKeyDuties] = useState<string[]>([]);
  const [requiredCertificatesAndLicenses, setRequiredCertificatesAndLicenses] = useState<any[]>([]);
  const [preferredQualifications, setPreferredQualifications] = useState<any[]>([]);
  const [jobGradeId, setJobGradeId] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (jobTitles.length === 0) {
      fetchJobTitles();
    }
    if (jobGrades.length === 0) {
      fetchJobGrades();
    }
  }, []);

  useEffect(() => {
    const fetchJobRole = async () => {
      if (!id) {
        setLoading(false);
        setHasAccess(false);
        return;
      }

      setLoading(true);
      try {
        // First try to find in context
        const existingRole = jobRoles.find(r => r.id == id);
        if (existingRole) {
          setJobRole(existingRole);
          setTitle(existingRole.title);
          setDescription(existingRole.description || "");
          setDepartmentId(existingRole.departmentId || "");
          setOutcomes(existingRole.outcomes || []);
          setKeyDuties(existingRole.keyDuties || []);
          setRequiredCertificatesAndLicenses(existingRole.requiredCertificatesAndLicenses || []);
          setPreferredQualifications(existingRole.preferredQualifications || []);
          setJobGradeId(existingRole.jobGradeId || "");
          setHasAccess(true);
          setLoading(false);
          return;
        }

        // Otherwise fetch from API
        const response = await getJobRole(id);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.role) {
            const mappedRole = mapJobRole(data.role);
            setJobRole(mappedRole);
            setTitle(mappedRole.title);
            setDescription(mappedRole.description || "");
            setDepartmentId(mappedRole.departmentId || "");
            setOutcomes(mappedRole.outcomes || []);
            setKeyDuties(mappedRole.keyDuties || []);
            setRequiredCertificatesAndLicenses(mappedRole.requiredCertificatesAndLicenses || []);
            setPreferredQualifications(mappedRole.preferredQualifications || []);
            setJobGradeId(mappedRole.jobGradeId || "");
            setHasAccess(true);
          } else {
            setHasAccess(false);
          }
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error("Error fetching job role:", error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    fetchJobRole();
  }, [id, jobRoles, mapJobRole]);

  if (loading) {
    return <LoadingPageSkeleton />;
  }

  if (!hasAccess || !jobRole) {
    return <AccessRestricted backMessage="Back to Job Roles" navigateTo="/job-roles" accessMessage="You don't have permission to view this job role." />;
  }

  const handleSave = async () => {
    if (!title || !departmentId) {
      toast.error("Job title and department are required");
      return;
    }

    setSaving(true);

    try {
      const filteredOutcomes = outcomes.filter(o => o.trim() !== "");
      const filteredDuties = keyDuties.filter(d => d.trim() !== "");

      const existingLog = jobRole.activityLogs || [];
      const timestamp = new Date().toISOString();
      const newLogEntry = {
        id: Date.now().toString(),
        timestamp,
        userId: currentUser?.id || "",
        changeDescription: "Updated job role details"
      };

      await updateJobRole(id!, {
        title,
        departmentId,
        description,
        outcomes: filteredOutcomes,
        keyDuties: filteredDuties,
        requiredCertificatesAndLicenses: requiredCertificatesAndLicenses?.map((c: any) => c.id) || [],
        preferredQualifications: preferredQualifications?.map((c: any) => c.id) || [],
        jobGradeId: jobGradeId || null,
        activityLogs: [...existingLog, newLogEntry]
      });

      navigate(`/job-roles/${id}`);
    } catch (error) {
      toast.error("Failed to update job role");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/job-roles/${id}`);
  };

  return (
    <Can I={ACTIONS.UPDATE} a={RESOURCES.JOB_ROLE} passThrough={<AccessRestricted />}>
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={handleCancel} className="flex-shrink-0">
              <ArrowLeft size={16} />
            </Button>
            <div className="flex items-center gap-2">
              <Briefcase size={20} />
              <h1 className="text-xl md:text-2xl font-bold">Edit Job Role</h1>
            </div>
          </div>
          <div className="flex gap-2 w-full md:w-auto">
            <Button variant="outline" onClick={handleCancel} className="w-full md:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="w-full md:w-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Job Role Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="title">Job Title<span className="text-destructive">*</span></Label>
              {(() => {
                const availableOptions = jobTitles
                  .filter(jt => !jt.deleted)
                  .map(item => ({ label: item.name, value: item.name }));
                const currentDeleted = jobTitles.find(jt => jt.name === title && jt.deleted);
                const options = currentDeleted
                  ? [{ label: `${currentDeleted.name} (deleted)`, value: currentDeleted.name }, ...availableOptions]
                  : availableOptions;
                return (
                  <Combobox
                    options={options}
                    value={title}
                    onChange={(value) => setTitle(value as JobRoleTitleType)}
                  />
                );
              })()}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="department">Department<span className="text-destructive">*</span></Label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.departmentCode} - {dept.departmentName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Job role description"
                rows={4}
              />
            </div>

            <div className="grid gap-2">
              <Label>Required Certificates & Licenses</Label>
              <MultiSelect
                options={qualificationPresets.map(p => ({ label: p.name, value: p.id }))}
                onChange={(values: string[]) => {
                  const selectedPresets = qualificationPresets.filter(p => values.includes(p.id));
                  setRequiredCertificatesAndLicenses(selectedPresets);
                }}
                selected={requiredCertificatesAndLicenses?.map((c: any) => c.id) || []}
                placeholder="Select required certificates"
                maxDisplayTags={3}
              />
            </div>

            <div className="grid gap-2">
              <Label>Preferred Qualifications</Label>
              <MultiSelect
                options={qualificationPresets.map(p => ({ label: p.name, value: p.id }))}
                onChange={(values: string[]) => {
                  const selectedPresets = qualificationPresets.filter(p => values.includes(p.id));
                  setPreferredQualifications(selectedPresets);
                }}
                selected={preferredQualifications?.map((c: any) => c.id) || []}
                placeholder="Select preferred qualifications"
                maxDisplayTags={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="jobGrade">Grading Scale</Label>
              <Select value={jobGradeId || "none"} onValueChange={(val) => setJobGradeId(val === "none" ? "" : val)}>
                <SelectTrigger id="jobGrade">
                  <SelectValue placeholder="Select grading scale" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {jobGrades.filter(g => !g.deleted).map((grade) => (
                    <SelectItem key={grade.id} value={grade.id}>{grade.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label>Key Duties</Label>
              <div className="space-y-2 border border-gray-200 rounded-md p-3">
                {keyDuties.map((duty, index) => (
                  <div key={`duty-${index}`} className="flex gap-2">
                    <Input
                      value={duty}
                      onChange={(e) => {
                        const newDuties = [...keyDuties];
                        newDuties[index] = e.target.value;
                        setKeyDuties(newDuties);
                      }}
                      placeholder={`Key duty ${index + 1}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Move to Outcomes"
                      onClick={() => {
                        const newDuties = keyDuties.filter((_, i) => i !== index);
                        const newOutcomes = [...outcomes, duty];
                        setKeyDuties(newDuties);
                        setOutcomes(newOutcomes);
                      }}
                    >
                      ↓
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newDuties = keyDuties.filter((_, i) => i !== index);
                        setKeyDuties(newDuties);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {keyDuties.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No key duties added yet</p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setKeyDuties([...keyDuties, ""])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Key Duty
                </Button>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Outcomes</Label>
              <div className="space-y-2 border border-gray-200 rounded-md p-3">
                {outcomes.map((outcome, index) => (
                  <div key={`outcome-${index}`} className="flex gap-2">
                    <Input
                      value={outcome}
                      onChange={(e) => {
                        const newOutcomes = [...outcomes];
                        newOutcomes[index] = e.target.value;
                        setOutcomes(newOutcomes);
                      }}
                      placeholder={`Outcome ${index + 1}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Move to Key Duties"
                      onClick={() => {
                        const newOutcomes = outcomes.filter((_, i) => i !== index);
                        const newDuties = [...keyDuties, outcome];
                        setOutcomes(newOutcomes);
                        setKeyDuties(newDuties);
                      }}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newOutcomes = outcomes.filter((_, i) => i !== index);
                        setOutcomes(newOutcomes);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {outcomes.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No outcomes added yet</p>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOutcomes([...outcomes, ""])}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Outcome
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Can>
  );
}
